import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { ecityCertificationsService } from '../../../services/trainingExchangeService';
import { partnerCertificationsService } from '../../../services/enablementService';
import { partnerService } from '../../../services/partnerService';
import { useToast } from '../../../contexts/ToastContext';

const IssueCertModal = ({ partners, onClose, onSuccess }) => {
  const [form, setForm] = useState({ cert_name: '', issued_by_partner_id: '', issued_by_name: '', issued_to_name: '', issued_to_email: '', issued_to_department: '', issue_date: new Date()?.toISOString()?.split('T')?.[0], expiry_date: '', cert_level: 'standard' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const handleChange = (e) => setForm(p => ({ ...p, [e?.target?.name]: e?.target?.value }));
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.cert_name?.trim() || !form?.issued_to_name?.trim()) { setError('Certificate name and recipient are required.'); return; }
    setSaving(true); setError(null);
    try { await ecityCertificationsService?.create({ ...form, cert_status: 'active' }); onSuccess(); }
    catch (err) { setError(err?.message || 'Failed to issue certificate.'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-24 py-18 border-b border-slate-100 bg-gradient-to-r from-amber-50 to-yellow-50 flex-shrink-0">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center">
              <Icon name="Award" size={18} className="text-white" />
            </div>
            <h2 className="text-sm font-bold text-slate-900">Issue Certificate</h2>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500"><Icon name="X" size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-24 space-y-14 overflow-y-auto flex-1">
          {error && <div className="flex items-center gap-10 p-12 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><Icon name="AlertCircle" size={15} />{error}</div>}
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Certificate Name *</label><input name="cert_name" value={form?.cert_name} onChange={handleChange} placeholder="e.g. Cisco Certified Network Associate" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20" required /></div>
          <div className="grid grid-cols-2 gap-12">
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Issuing Partner</label>
              <select name="issued_by_partner_id" value={form?.issued_by_partner_id} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
                <option value="">Select partner...</option>
                {partners?.map(p => <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>)}
              </select></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Issuer Name</label><input name="issued_by_name" value={form?.issued_by_name} onChange={handleChange} placeholder="Issuer's name" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20" /></div>
          </div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Issued To (Name) *</label><input name="issued_to_name" value={form?.issued_to_name} onChange={handleChange} placeholder="Recipient full name" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20" required /></div>
          <div className="grid grid-cols-2 gap-12">
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Recipient Email</label><input type="email" name="issued_to_email" value={form?.issued_to_email} onChange={handleChange} placeholder="email@ecity.com" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20" /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Department</label><input name="issued_to_department" value={form?.issued_to_department} onChange={handleChange} placeholder="e.g. Technology" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20" /></div>
          </div>
          <div className="grid grid-cols-3 gap-12">
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Issue Date</label><input type="date" name="issue_date" value={form?.issue_date} onChange={handleChange} className="w-full px-10 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none" /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Expiry Date</label><input type="date" name="expiry_date" value={form?.expiry_date} onChange={handleChange} className="w-full px-10 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none" /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Level</label>
              <select name="cert_level" value={form?.cert_level} onChange={handleChange} className="w-full px-10 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
                {['standard', 'associate', 'professional', 'expert']?.map(l => <option key={l} value={l}>{l?.charAt(0)?.toUpperCase() + l?.slice(1)}</option>)}
              </select></div>
          </div>
          <div className="flex gap-12 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-16 py-10 text-sm font-semibold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-16 py-10 text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-yellow-500 rounded-xl hover:opacity-90 disabled:opacity-60">
              {saving ? 'Issuing...' : 'Issue Certificate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CertificationWallet = () => {
  const [eCityCerts, setECityCerts] = useState([]);
  const [partnerCerts, setPartnerCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterPartner, setFilterPartner] = useState('');
  const [search, setSearch] = useState('');
  const [issueModal, setIssueModal] = useState(false);
  const [partners, setPartners] = useState([]);
  const { showToast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const [ec, pc] = await Promise.all([
        ecityCertificationsService?.getAll({ status: filterStatus !== 'all' ? filterStatus : undefined, partnerId: filterPartner || undefined, search: search || undefined }),
        partnerCertificationsService?.getAll(filterPartner || null)
      ]);
      setECityCerts(ec);
      setPartnerCerts(pc);
    } catch (e) { showToast?.('Failed to load certificates', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterStatus, filterPartner, search]);
  useEffect(() => { partnerService?.getAll?.()?.then(setPartners)?.catch(() => {}); }, []);

  const getExpiryStatus = (expiryDate, certStatus) => {
    if (certStatus === 'expired' || certStatus === 'revoked') return { label: certStatus?.charAt(0)?.toUpperCase() + certStatus?.slice(1), color: 'bg-red-100 text-red-700', dot: 'bg-red-500' };
    if (!expiryDate) return { label: 'No Expiry', color: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' };
    const days = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return { label: 'Expired', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' };
    if (days <= 30) return { label: `Expires in ${days}d`, color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' };
    return { label: 'Valid', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' };
  };

  const allCerts = [
    ...(filterSource !== 'partner' ? eCityCerts?.map(c => ({ ...c, _source: 'partner_to_ecity', _issuedTo: c?.issued_to_name, _issuedBy: c?.issued_by_name || c?.partners?.brand_name || c?.partners?.legal_name || 'Partner', _certName: c?.cert_name, _issueDate: c?.issue_date, _expiryDate: c?.expiry_date, _certStatus: c?.cert_status, _level: c?.cert_level })) : []),
    ...(filterSource !== 'ecity' ? partnerCerts?.map(c => ({ ...c, _source: 'ecity_to_partner', _issuedTo: c?.partners?.brand_name || c?.partners?.legal_name || 'Partner', _issuedBy: 'eCity', _certName: c?.cert_name, _issueDate: c?.issue_date, _expiryDate: c?.expiry_date, _certStatus: c?.cert_status, _level: c?.cert_level })) : []),
  ];

  const expiredCount = allCerts?.filter(c => { const s = getExpiryStatus(c?._expiryDate, c?._certStatus); return s?.label === 'Expired'; })?.length;
  const expiringCount = allCerts?.filter(c => { const s = getExpiryStatus(c?._expiryDate, c?._certStatus); return s?.label?.startsWith('Expires in'); })?.length;
  const validCount = allCerts?.filter(c => { const s = getExpiryStatus(c?._expiryDate, c?._certStatus); return s?.label === 'Valid' || s?.label === 'No Expiry'; })?.length;

  return (
    <div>
      <div className="mb-20 p-16 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl border border-amber-100">
        <div className="flex items-center gap-12 mb-6">
          <Icon name="Wallet" size={18} className="text-amber-600" />
          <h2 className="text-base font-bold text-slate-900">Certification Wallet</h2>
        </div>
        <p className="text-sm text-slate-600">All certificates in one place — eCity-issued to partners and partner-issued to eCity members.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-12 mb-24">
        <div className="bg-emerald-50 rounded-2xl p-16 text-center">
          <p className="text-2xl font-bold text-emerald-600">{validCount}</p>
          <p className="text-xs text-slate-600 mt-4 flex items-center justify-center gap-6"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />Valid</p>
        </div>
        <div className="bg-amber-50 rounded-2xl p-16 text-center">
          <p className="text-2xl font-bold text-amber-600">{expiringCount}</p>
          <p className="text-xs text-slate-600 mt-4 flex items-center justify-center gap-6"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />Expiring Soon</p>
        </div>
        <div className="bg-red-50 rounded-2xl p-16 text-center">
          <p className="text-2xl font-bold text-red-600">{expiredCount}</p>
          <p className="text-xs text-slate-600 mt-4 flex items-center justify-center gap-6"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" />Expired</p>
        </div>
      </div>

      {/* Filters + Issue Button */}
      <div className="flex flex-col sm:flex-row gap-12 items-start sm:items-center justify-between mb-20">
        <div className="flex flex-wrap gap-12">
          <div className="relative">
            <Icon name="Search" size={15} className="absolute left-12 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e?.target?.value)} placeholder="Search by name..." className="pl-36 pr-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20 w-48" />
          </div>
          <select value={filterSource} onChange={e => setFilterSource(e?.target?.value)} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
            <option value="all">All Sources</option>
            <option value="ecity">eCity → Partner</option>
            <option value="partner">Partner → eCity</option>
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
            {['all', 'active', 'expired', 'revoked']?.map(s => <option key={s} value={s}>{s === 'all' ? 'All Statuses' : s?.charAt(0)?.toUpperCase() + s?.slice(1)}</option>)}
          </select>
          <select value={filterPartner} onChange={e => setFilterPartner(e?.target?.value)} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
            <option value="">All Partners</option>
            {partners?.map(p => <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>)}
          </select>
        </div>
        <button onClick={() => setIssueModal(true)} className="flex items-center gap-8 px-16 py-10 text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-yellow-500 rounded-xl hover:opacity-90 transition-opacity flex-shrink-0">
          <Icon name="Plus" size={15} />Issue Certificate
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-48"><div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : allCerts?.length === 0 ? (
        <div className="text-center py-48 text-slate-500"><Icon name="Wallet" size={40} className="mx-auto mb-12 opacity-30" /><p className="font-medium">No certificates found</p></div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>{['Certificate', 'Issued By', 'Issued To', 'Source', 'Issue Date', 'Expiry', 'Status']?.map(h => <th key={h} className="px-16 py-12 text-left text-xs font-semibold text-slate-600">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {allCerts?.map((cert, idx) => {
                const expiry = getExpiryStatus(cert?._expiryDate, cert?._certStatus);
                return (
                  <tr key={`${cert?._source}-${cert?.id}-${idx}`} className="hover:bg-slate-50 transition-colors">
                    <td className="px-16 py-12">
                      <div className="flex items-center gap-10">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-100 to-yellow-100 flex items-center justify-center flex-shrink-0">
                          <Icon name="Award" size={14} className="text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 text-xs">{cert?._certName}</p>
                          {cert?._level && <p className="text-xs text-slate-500 capitalize">{cert?._level}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-16 py-12 text-xs text-slate-700">{cert?._issuedBy}</td>
                    <td className="px-16 py-12 text-xs text-slate-700">{cert?._issuedTo}</td>
                    <td className="px-16 py-12">
                      <span className={`px-8 py-3 text-xs font-semibold rounded-full ${cert?._source === 'partner_to_ecity' ? 'bg-violet-100 text-violet-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {cert?._source === 'partner_to_ecity' ? 'Partner → eCity' : 'eCity → Partner'}
                      </span>
                    </td>
                    <td className="px-16 py-12 text-xs text-slate-500">{cert?._issueDate ? new Date(cert?._issueDate)?.toLocaleDateString() : '—'}</td>
                    <td className="px-16 py-12 text-xs text-slate-500">{cert?._expiryDate ? new Date(cert?._expiryDate)?.toLocaleDateString() : 'No Expiry'}</td>
                    <td className="px-16 py-12">
                      <span className={`flex items-center gap-6 px-10 py-4 text-xs font-semibold rounded-full w-fit ${expiry?.color}`}>
                        <span className={`w-2 h-2 rounded-full ${expiry?.dot}`} />{expiry?.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {issueModal && <IssueCertModal partners={partners} onClose={() => setIssueModal(false)} onSuccess={() => { setIssueModal(false); load(); showToast?.('Certificate issued successfully', 'success'); }} />}
    </div>
  );
};

export default CertificationWallet;
